import React, { useCallback, useEffect, useState } from 'react';
import { CustomButton } from '../Button/Button';
import styles from './AddClipContent.module.scss';
import SelectFromLibraryTab from '../SelectFromLibraryTab/SelectFromLibraryTab';
import { useClipsContext } from '../../context/ClipsContext';
import FloatingTooltip from '../FloatingTooltip/FloatingTooltip';
import { useTranslation } from 'react-i18next';
import CloseButton from '../CloseButton/CloseButton';
import { MAX_NAME_LENGTH } from '../../../common/constants/common';
import SearchComponentWithTags from '../SearchComponentWithTags/SearchComponentWithTags';

const AddClipContent = ({ onClose, onSave }) => {
  const [searchInput, setSearchValue] = useState({});
  const [chosenFolder, setChosenFolder] = useState(null);
  const [selectedClipsNumber, setSelectedClipsNumber] = useState(0);
  const [titleHover, setTitleHover] = useState(false);
  const [currentFolderName, setCurrentFolderName] = useState('');
  const [executing, setExecuting] = useState(false);

  const { selectedClips, foldersWithClips, toggleUnselectClips } = useClipsContext();

  const { t } = useTranslation();

  const handleFolderChoose = folderId => {
    setChosenFolder(folderId);
  };

  const handleBackToLibrary = () => {
    handleFolderChoose(null);
  };

  const handleSaveClips = useCallback(() => {
    setExecuting(true);
    onSave(Object.values(selectedClips));
    onClose();
  }, [onSave, selectedClips, onClose]);

  useEffect(() => {
    return () => toggleUnselectClips();
  }, []);

  const onInputChange = value => {
    if (typeof value === 'string') {
      setSearchValue(prevState => ({ ...prevState, query: value }));
    } else {
      setSearchValue(prevState => ({ ...prevState, tags: value }));
    }
  };

  useEffect(() => {
    if (chosenFolder) {
      setSelectedClipsNumber(foldersWithClips[chosenFolder] ? foldersWithClips[chosenFolder] : 0);
    } else {
      setSelectedClipsNumber(Object.keys(selectedClips).length);
    }
  }, [selectedClips, chosenFolder]);

  const updateFolderName = name => {
    setCurrentFolderName(name);
  };

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.headerContainer}>
        <div className={styles.titleWrapper}>
          <h1 className={styles.header}>{t('clip_organizer.add_clip.modal.header')}</h1>
          <SearchComponentWithTags searchValue={searchInput} onChange={onInputChange} clearButton maxLength={MAX_NAME_LENGTH} />
        </div>
        <CloseButton onClose={onClose} />
      </div>
      <div className={styles.tabComponentWrapper}>
        <SelectFromLibraryTab
          searchInput={searchInput}
          chosenFolder={chosenFolder}
          handleFolderChoose={handleFolderChoose}
          currentFolderName={currentFolderName}
          updateFolderName={updateFolderName}
        />
      </div>
      <div className={styles.modalFooter}>
        <p className={styles.selectedAmount}>
          {`${selectedClipsNumber} video${selectedClipsNumber !== 1 ? 's' : ''} ${t('clip_organizer.add_modal.selected_number')}`}
          {chosenFolder ? (
            <span
              className={styles.folderName}
              onMouseOver={() => setTitleHover(true)}
              onMouseLeave={() => setTitleHover(false)}
            >{`in ${currentFolderName}`}</span>
          ) : null}
          {titleHover && chosenFolder && <FloatingTooltip label={currentFolderName} offset={30} />}
        </p>
        <div className={styles.btnsContainer}>
          {chosenFolder ? (
            <CustomButton
              label={t('clip_organizer.add_modal.back_to_library')}
              variant="secondary"
              className={styles.controlBtn}
              onClick={handleBackToLibrary}
            />
          ) : (
            <CustomButton label={t('general.cancel')} variant="secondary" className={styles.controlBtn} onClick={onClose} />
          )}
          <CustomButton
            label={t('general.add')}
            variant="primary"
            className={styles.controlBtn}
            disabled={executing || Object.keys(selectedClips).length === 0}
            onClick={handleSaveClips}
          />
        </div>
      </div>
    </div>
  );
};

export default AddClipContent;
